import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import {
  MODULE_LABEL,
  getStoreHome,
  getStoreModule,
  getStoreModuleLabel,
} from "../utils/moduleNav";
import LoadingScreen from "./LoadingScreen";

/* ============================================================================
 * In-app store switcher
 * ----------------------------------------------------------------------------
 * A store belongs to a group (store_master.common_store) and one person may be
 * spread across several of them — commonly a Travel ERP store and a pawn store.
 * This lets them move between those without logging out.
 *
 * The type label on every row is the point of the screen, not decoration: the
 * pawn module's screens are exclusive to is_pawn stores, so the person has to be
 * told which module they are about to land in, and the switch itself has to
 * carry them to that module's home rather than reloading the pawn route they
 * happened to be on (see switchStore's `redirectTo`).
 * ========================================================================== */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

const BLUE = "#2563EB";
const INK = "#0F172A";
const INK_MUTED = "#64748B";
const INK_FAINT = "#94A3B8";
const BORDER = "#E2E8F0";

// One badge per host module, so "this is a pawn store" reads at a glance.
const MODULE_BADGE = {
  pawn: { fg: "#1E40AF", bg: "#EFF6FF", border: "#BFDBFE" },
  travel: { fg: "#B45309", bg: "#FEF3C7", border: "#FDE68A" },
  dailytick: { fg: "#334155", bg: "#F1F5F9", border: "#E2E8F0" },
};

const readStored = (key) => {
  try {
    return JSON.parse(localStorage.getItem(`CapacitorStorage.${key}`)) || {};
  } catch {
    return {};
  }
};

/**
 * How a target store relates to the one we are in — a sibling in the same store
 * group, or, when the current store belongs to no group, simply another store
 * this person is a user of. `is_parent` describes a store's own group, so it only
 * means anything about *this* switch when the two share that group.
 */
const storeRelation = (store) => {
  if (!store.is_connected) return "Your store";
  return store.is_parent ? "Parent store" : "Linked store";
};

/**
 * The branch a switch into this store would land in, when saying so adds
 * anything.
 *
 * getSwitchableStores serves each row with its MAIN branch flattened on as
 * `branch_name`, which is where a switch lands unless this device remembers a
 * different branch for that store — and it only can if the person has been
 * there before.
 *
 * Suppressed when it would repeat the store's own name: migration 015 named
 * each store's first branch after the store, so on a single-branch store the
 * chip would say nothing twice.
 */
const branchNoteFor = (store) => {
  const branch = store?.branch_name;
  if (!branch) return null;

  const sameAsStore =
    String(branch).trim().toLowerCase() ===
    String(store?.store_name ?? "").trim().toLowerCase();

  return sameAsStore ? null : branch;
};

/**
 * Stores this session may move to. The server scopes them to the store group
 * ("family") of the store we are in — never to everything this mobile owns, so
 * an owner with two unrelated groups can't hop between them here — falling back
 * to this person's other ungrouped stores when the current store has no group.
 * Fetched once per store so the caller can hide its switch control entirely when
 * there is nowhere to go.
 *
 *   const { stores, loading } = useSwitchableStores();
 *   stores.length > 0 && <button …/>
 */
export const useSwitchableStores = () => {
  const { currentStoreInfo, userInfo } = useContext(AutoTaskAuthContext) || {};

  // Context wins once hydrated; storage covers the first render and the pawn
  // routes, which are reachable without the context ever being populated.
  const store_id = (currentStoreInfo || readStored("current_store_info")).id;
  const mobile = (userInfo || readStored("user")).mobile;

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (store_id == null || !mobile) return;

    let cancelled = false;
    setLoading(true);

    axios
      .get(`${API_BASE_URL}/api/auth/switchable-stores/${store_id}`, {
        params: { mobile },
      })
      .then((res) => {
        if (!cancelled) setStores(res?.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setStores([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [store_id, mobile]);

  return { stores, loading };
};

const ModuleBadge = ({ module }) => {
  const tone = MODULE_BADGE[module] || MODULE_BADGE.dailytick;
  return (
    <Box
      sx={{
        px: 0.75,
        py: 0.125,
        borderRadius: "6px",
        flexShrink: 0,
        fontSize: "0.65rem",
        fontWeight: 700,
        lineHeight: 1.6,
        whiteSpace: "nowrap",
        color: tone.fg,
        bgcolor: tone.bg,
        border: `1px solid ${tone.border}`,
      }}
    >
      {MODULE_LABEL[module] || MODULE_LABEL.dailytick}
    </Box>
  );
};

/**
 * The picker itself. `stores` / `loading` come from useSwitchableStores so the
 * host can gate its own control on the same data without a second fetch — and so
 * each host decides what belongs in its own list (the pawn rail passes pawn
 * stores only; the Daily Tick sidebar passes the whole group). `note` explains
 * that narrowing to whoever is looking at the shorter list.
 */
export const StoreSwitcherDialog = ({
  open,
  onClose,
  stores = [],
  loading,
  note,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { currentStoreInfo, switchStore } =
    useContext(AutoTaskAuthContext) || {};

  const activeStore = currentStoreInfo || readStored("current_store_info");
  const [switchingTo, setSwitchingTo] = useState(null);
  const [error, setError] = useState(null);

  // switchStore navigates (or reloads) on success, so only failure needs
  // handling here — the overlay otherwise stays up until the page is replaced.
  // The dialog closes first: its z-index sits above the loader on mobile.
  const handleSwitch = async (store) => {
    onClose?.();
    setSwitchingTo(store);
    try {
      await switchStore(store.store_id, { redirectTo: getStoreHome(store) });
    } catch (err) {
      setSwitchingTo(null);
      setError(err?.message || "Failed to switch store");
    }
  };

  return (
    <>
      {/* zIndex is set explicitly because the app chrome outranks MUI's modal
          layer: the Daily Tick header is 2000 and the mobile drawer paper 5500,
          against a default Dialog of 1300. Left at the default the full-screen
          mobile dialog opened *underneath* the header — its title and close
          button hidden behind the logo bar — which read as "nothing happened".
          6000 clears the app chrome and still sits under the switching
          overlay. */}
      <Dialog
        open={!!open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        sx={{ zIndex: 6000 }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : "14px",
            // Full screen means the paper owns the notch/status-bar strip.
            ...(isMobile && { pt: "var(--safe-area-top, 0px)" }),
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5, pr: 6 }}>
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: INK }}>
            Switch store
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: INK_MUTED }}>
            Currently in {activeStore?.name || `store #${activeStore?.id}`} ·{" "}
            {getStoreModuleLabel(activeStore)}
          </Typography>
          {/* Full screen leaves no backdrop to tap, so the X is the only way
              out on a phone. */}
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              position: "absolute",
              top: isMobile ? "calc(var(--safe-area-top, 0px) + 8px)" : 8,
              right: 8,
              color: INK_MUTED,
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 1.5, pb: 2 }}>
          {note && (
            <Typography
              sx={{
                fontSize: "0.72rem",
                color: INK_MUTED,
                bgcolor: "#F8FAFC",
                border: `1px solid ${BORDER}`,
                borderRadius: "8px",
                px: 1.25,
                py: 0.75,
                mb: 1.25,
              }}
            >
              {note}
            </Typography>
          )}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} sx={{ color: BLUE }} />
            </Box>
          ) : stores.length === 0 ? (
            <Typography
              sx={{ fontSize: "0.85rem", color: INK_MUTED, px: 1, py: 2 }}
            >
              No other store is available to switch to.
            </Typography>
          ) : (
            <List disablePadding>
              {stores.map((store) => (
                <ListItemButton
                  key={store.store_id}
                  onClick={() => handleSwitch(store)}
                  sx={{
                    borderRadius: "10px",
                    border: `1px solid ${BORDER}`,
                    mb: 1,
                    gap: 1.5,
                  }}
                >
                  <StorefrontRoundedIcon sx={{ fontSize: 20, color: BLUE }} />
                  <ListItemText
                    disableTypography
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          noWrap
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            color: INK,
                            minWidth: 0,
                          }}
                        >
                          {store.store_name || `Store #${store.store_id}`}
                        </Typography>
                        <ModuleBadge module={getStoreModule(store)} />
                      </Box>
                    }
                    secondary={
                      <Typography
                        sx={{ fontSize: "0.72rem", color: INK_FAINT }}
                      >
                        {storeRelation(store)}
                        {store.role_label ? ` · ${store.role_label}` : ""}
                        {branchNoteFor(store)
                          ? ` · ${branchNoteFor(store)}`
                          : ""}
                      </Typography>
                    }
                  />
                  <ArrowForwardRoundedIcon
                    sx={{ fontSize: 18, color: INK_FAINT }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      {/* Held until the navigation replaces the page, so the switch never shows
          half-updated data behind it. */}
      {switchingTo && (
        <LoadingScreen
          loadingMessage={`Switching to ${switchingTo.store_name || "store"}`}
        />
      )}

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default StoreSwitcherDialog;
